import React, { useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { snakeCase } from "lodash";
import request from "common/request";
import { setSuccessMessage } from "../../page-notifications/page-notifications-slice";
import ReceiptModal from "../receipt-modal";
import ActionsDropdown from "./actions-dropdown";
import AdminActionModal from "./admin-action-modal";
import CancelLinkModal from "./cancel-link-modal";
import AddCouponModal from "./add-coupon-modal";
import GenerateAddressModal from "./generate-address-modal";

const propTypes = {
  actionLinks: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      link: PropTypes.string,
      link_options: PropTypes.shape({
        method: PropTypes.string,
        target: PropTypes.string
      }),
      modal_data: PropTypes.shape({
        modal_type: PropTypes.string,
        processing_message: PropTypes.string,
        confirmation_prompt: PropTypes.string,
        cancel_copy: PropTypes.string,
        text: PropTypes.string
      })
    })
  ).isRequired,
  networkConsoleLinks: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, url: PropTypes.string })
  ).isRequired,
  onRefetch: PropTypes.func.isRequired
};

const PROMPT_FIELD = {
  admin_action_request: "confirmation_prompt",
  cancel: "cancel_copy",
  confirmation: "text"
};

const AdminActions = ({ actionLinks, networkConsoleLinks, onRefetch }) => {
  const dispatch = useDispatch();
  const [activeItem, setActiveItem] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [networkError, setNetworkError] = useState(null);

  const handleClose = () => {
    setActiveItem(null);
    setNetworkError(null);
    setIsProcessing(false);
  };

  const handleSuccess = (successMsg = null) => {
    if (successMsg) dispatch(setSuccessMessage([successMsg]));
    if (onRefetch) onRefetch();
    handleClose();
  };

  const getConfirmationPrompt = item => {
    const { modal_type: modalType, ...rest } = item?.modal_data || {};
    const field = PROMPT_FIELD[modalType];
    return rest[field];
  };

  const getRequestParams = item => {
    if (item?.modal_data?.modal_type === "cancel") return { from: item.modal_data.from };
    return {};
  };

  const submitRequest = async item => {
    const method = item.link_options?.method || "post";
    setIsProcessing(true);
    try {
      const response = await request[method](item.link, getRequestParams(item));
      const redirectTo = response.data?.redirectPath || response.data?.redirect_url;
      const successMsg = response.data?.success_message;
      if (redirectTo) {
        window.location.href = redirectTo;
      } else {
        handleSuccess(successMsg);
      }
    } catch (e) {
      setIsProcessing(false);
      setNetworkError({
        title: e.response?.data?.error || e.name,
        message: e.response?.data?.backtrace?.join("\n") || e?.message,
        additionalDetails: e.response?.data?.additional_details
      });
    }
  };

  const handleItemClick = item => {
    setActiveItem(item);
    if (
      item.modal_data?.modal_type === "admin_action_request" &&
      !item.modal_data?.confirmation_prompt
    ) {
      submitRequest(item);
    }
  };

  const confirmationPrompt = getConfirmationPrompt(activeItem);
  const activeModalType = activeItem?.modal_data?.modal_type;
  const isAdminActionModalOpen =
    ["admin_action_request", "cancel", "confirmation"].includes(activeModalType) &&
    (!!confirmationPrompt || isProcessing || !!networkError);

  const dropdownItems = [
    ...networkConsoleLinks.map(item => ({
      label: item.label,
      link: item.url,
      target: "_blank",
      action: "network_console_link_admin"
    })),
    ...(networkConsoleLinks.length > 0 ? [{ divider: true }] : []),
    ...actionLinks.map(item => {
      const action = `${snakeCase(item.label)}_admin`;
      if (item.modal_data)
        return { label: item.label, onClick: () => handleItemClick(item), action };
      if (item.link_options?.method && item.link_options.method !== "get")
        return { label: item.label, onClick: () => submitRequest(item), action };
      return { label: item.label, link: item.link, target: item.link_options?.target, action };
    })
  ];

  return (
    <>
      <ActionsDropdown
        label="Admin Actions"
        icon="lock"
        menuClassName="admin-actions-menu"
        items={dropdownItems}
      />
      <AdminActionModal
        isOpen={isAdminActionModalOpen}
        onClose={handleClose}
        onConfirm={() => submitRequest(activeItem)}
        confirmationPrompt={confirmationPrompt}
        isProcessing={isProcessing}
        processingMessage={activeItem?.modal_data?.processing_message}
        error={networkError}
      />
      {activeModalType === "receipt" && (
        <ReceiptModal
          display
          toggleDisplay={handleClose}
          receiptData={activeItem.modal_data.receipt}
          showNotification={activeItem.modal_data.show_notification}
        />
      )}
      {activeModalType === "cancel_link" && (
        <CancelLinkModal
          display
          toggleDisplay={handleClose}
          cancelLink={activeItem.modal_data.cancel_link}
        />
      )}
      {activeModalType === "add_coupon" && (
        <AddCouponModal
          isOpen
          onClose={handleClose}
          onSuccess={handleSuccess}
          availableCoupons={activeItem.modal_data.available_coupons}
          attachedCoupons={activeItem.modal_data.attached_coupons}
          requestPath={activeItem.link}
          campaignTypeSnake={activeItem.modal_data.campaign_type_snake}
        />
      )}
      {activeModalType === "generate_address" && (
        <GenerateAddressModal
          isOpen
          onClose={handleClose}
          onSuccess={handleSuccess}
          requestUrl={activeItem.link}
          campaignName={activeItem.modal_data.campaign_name}
          currentEmail={activeItem.modal_data.current_email}
        />
      )}
    </>
  );
};

AdminActions.propTypes = propTypes;

export default AdminActions;
